import React, { useContext } from 'react';
import { Close, Email, Phone, Business, Schedule, Info, Person, LocalOffer, Description, Public } from '@mui/icons-material';
import { Typography, Chip, Box } from '@mui/material';
import DrawerContext from '../../../context/DrawerContext';
import "../../../styles/Drawer.scss";
import "../../../Pages/CouponLeads/CouponLeads.scss";

const ViewLeadDrawer = ({ drawerDetails }) => {
  const drawerCtx = useContext(DrawerContext);

  const lead = drawerDetails;

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      return new Date(dateString).toLocaleDateString('en-GB', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const renderStatusBadge = (status) => {
    if (status === 'draft') {
      return <div className="status-draft">Draft</div>;
    }
    
    if (status === 'pending') {
      return <div className="status-pending">Pending</div>;
    }
    
    if (status === 'contacted') {
      return <div className="status-invite">Contacted</div>;
    }
    
    if (status === 'completed') {
      return <div className="status-active">Completed</div>;
    }
    
    return status ? <div className="status-unknown">{status}</div> : null;
  };

  const InfoRow = ({ icon: Icon, label, value, link = null, isBadge = false, badgeComponent = null, hideIfEmpty = false }) => {
    if (hideIfEmpty && (!value || value === 'N/A')) return null;
    
    const displayValue = value && value !== 'N/A' ? value : null;
    
    return (
      <div className="info-row">
        <div className="info-row-content">
          {Icon && <Icon className="info-icon" />}
          <Typography variant="body2" className="info-label">
            {label}:
          </Typography>
          <div className="info-value">
            {isBadge && badgeComponent ? (
              badgeComponent
            ) : displayValue ? (
              link ? (
                <a 
                  href={link} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="info-link"
                >
                  {displayValue}
                </a>
              ) : (
                <Typography variant="body2" className="info-text">
                  {displayValue}
                </Typography>
              )
            ) : (
              <Typography variant="body2" className="info-placeholder">
                Not provided
              </Typography>
            )}
          </div>
        </div>
      </div>
    );
  };

  const SectionHeader = ({ icon: Icon, title }) => (
    <div className="section-header">
      {Icon && <Icon className="section-icon" />}
      <Typography variant="h6" className="section-title">
        {title}
      </Typography>
    </div>
  );

  return (
    <div className="coupon-lead-drawer">
      <div className="details-section">
        <header>
          <h1>Lead Details</h1>
          <Close onClick={() => drawerCtx.closeDrawer()} className="close-icon" />
        </header>

        <section>
          {/* Company Information */}
          <div className="details drawer-section">
            <SectionHeader icon={Business} title="Company Information" />
            <div className="card drawer-card">
              <InfoRow 
                icon={Business}
                label="Company Name" 
                value={lead.companyName || lead.company_name}
              />
              <InfoRow 
                icon={Email} 
                label="Email" 
                value={lead.email}
                link={lead.email ? `mailto:${lead.email}` : null}
              />
              <InfoRow 
                icon={Phone} 
                label="Phone" 
                value={lead.phone}
                link={lead.phone ? `tel:${lead.phone}` : null}
              />
              {lead.website && (
                <InfoRow 
                  icon={Public}
                  label="Website"
                  value={lead.website}
                  link={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`}
                />
              )}
              {lead.description && (
                <InfoRow 
                  icon={Description}
                  label="Description" 
                  value={lead.description}
                />
              )}
            </div>
          </div>

          {/* Plan Information */}
          <div className="details drawer-section">
            <SectionHeader icon={LocalOffer} title="Plan Information" />
            <div className="card drawer-card">
              <InfoRow 
                label="Plan Name" 
                value={lead.planName || lead.plan_name}
              />
            </div>
          </div>

          {/* Location Information */}
          {(lead.city || lead.country) && (
            <div className="details drawer-section">
              <SectionHeader icon={Info} title="Location Information" />
              <div className="card drawer-card">
                {lead.city && (
                  <InfoRow 
                    label="City" 
                    value={lead.city}
                  />
                )}
                {lead.country && (
                  <InfoRow 
                    label="Country" 
                    value={lead.country}
                  />
                )}
              </div>
            </div>
          )}

          {/* Agent Information */}
          <div className="details drawer-section">
            <SectionHeader icon={Person} title="Agent Information" />
            <div className="card drawer-card">
              {lead.affiliate ? (
                <>
                  <InfoRow
                    label="Agent Name"
                    value={lead.affiliate.name || [lead.affiliate.first_name, lead.affiliate.last_name].filter(Boolean).join(' ') || null}
                  />
                  <InfoRow
                    label="Affiliate Code"
                    value={lead.affiliate.code}
                  />
                </>
              ) : (
                <InfoRow
                  label="Agent"
                  value="No agent associated with this lead."
                />
              )}
            </div>
          </div>

          {/* Additional Information */}
          <div className="details drawer-section">
            <SectionHeader icon={Info} title="Additional Information" />
            <div className="card drawer-card">
              <InfoRow 
                label="Status" 
                value={lead.status}
                isBadge={true}
                badgeComponent={renderStatusBadge(lead.status)}
              />
              <InfoRow 
                label="Created At" 
                value={formatDate(lead.created_at)}
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ViewLeadDrawer;

